import { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  MdAcUnit,
  MdAttachMoney,
  MdBuild,
  MdCardMembership,
  MdCheckCircle,
  MdConfirmationNumber,
  MdEvent,
  MdEventAvailable,
  MdFitnessCenter,
  MdLock,
  MdPayments,
  MdPersonAdd,
  MdSubscriptions,
  MdHowToReg,
} from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import PanelStatCard from '../../components/receptionist/PanelStatCard';
import { useGymSettings } from '../../context/GymSettingsContext';
import { fetchDashboardOverview } from '../../services/receptionist/dashboardData';
import { pkTodayKey, pkTodayRange } from '../../utils/pkTime';
import { RECEPTIONIST_SALE_PAGES } from './sales/salePageConfig';
import '../owner/ownerPages.css';
import './dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Filler,
  Legend,
);

const CHART_COLLECTION = '#f59e0b';
const CHART_MEMBERS = '#a855f7';
const CHART_SUBSCRIPTIONS = '#94ff00';
const CHART_RENEWAL = '#3b82f6';
const PIE_COLORS = [
  '#94ff00',
  '#f59e0b',
  '#3b82f6',
  '#a855f7',
  '#06b6d4',
  '#ef4444',
  '#22c55e',
  '#ec4899',
  '#14b8a6',
  '#eab308',
];
const TEXT_SECONDARY = '#94a3b8';
const GRID_COLOR = 'rgba(148, 163, 184, 0.08)';

function todayDashRange() {
  return pkTodayRange();
}

const saleStatIcons = Object.fromEntries(
  RECEPTIONIST_SALE_PAGES.map((page) => [page.source, page.icon]),
);

const statIcons = {
  members: MdPersonAdd,
  newMembership: MdHowToReg,
  newMembershipAmount: MdAttachMoney,
  registrationFee: MdCardMembership,
  subscriptions: MdSubscriptions,
  memberships: MdCardMembership,
  attendance: MdEventAvailable,
  classes: MdEvent,
  guestPass: MdConfirmationNumber,
  trainers: MdFitnessCenter,
  equipments: MdBuild,
  lockers: MdLock,
  remaining: MdPayments,
  payments: MdPayments,
  freeze: MdAcUnit,
  collection: MdAttachMoney,
  ...saleStatIcons,
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1A1F2B',
      borderColor: 'rgba(148, 163, 184, 0.14)',
      borderWidth: 1,
      titleColor: '#f1f5f9',
      bodyColor: TEXT_SECONDARY,
    },
  },
  scales: {
    x: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
    y: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
  },
};

function buildLineDataset(label, data, labels, color) {
  return {
    labels,
    datasets: [
      {
        label,
        data,
        borderColor: color,
        backgroundColor: `${color}24`,
        fill: true,
        tension: 0.35,
        pointBackgroundColor: color,
        pointBorderColor: '#0F1218',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };
}

function buildBarDataset(label, data, labels, color) {
  return {
    labels,
    datasets: [
      {
        label,
        data,
        backgroundColor: `${color}BF`,
        borderColor: color,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
}

function buildCompareBar(labels, values, colors) {
  return {
    labels,
    datasets: [
      {
        label: 'Amount',
        data: values,
        backgroundColor: colors.map((c) => `${c}BF`),
        borderColor: colors,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
}

function buildPieDataset(labels, values) {
  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: labels.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        borderColor: '#0F1218',
        borderWidth: 2,
      },
    ],
  };
}

const collectionChartOptions = {
  ...chartOptions,
  scales: {
    ...chartOptions.scales,
    y: {
      ...chartOptions.scales.y,
      ticks: {
        color: TEXT_SECONDARY,
        callback: (value) => `${(value / 1000).toFixed(0)}k`,
      },
    },
  },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { color: TEXT_SECONDARY, boxWidth: 12, padding: 12 },
    },
    tooltip: {
      backgroundColor: '#1A1F2B',
      borderColor: 'rgba(148, 163, 184, 0.14)',
      borderWidth: 1,
      titleColor: '#f1f5f9',
      bodyColor: TEXT_SECONDARY,
    },
  },
};

function ChartBlock({ title, type, data, options, full }) {
  let ChartComponent = Bar;
  if (type === 'line') ChartComponent = Line;
  if (type === 'doughnut' || type === 'pie') ChartComponent = Doughnut;
  return (
    <div className={`rec-dash-chart-item${full ? ' rec-dash-chart-item-full' : ''}`}>
      <h3 className="rec-dash-chart-label">{title}</h3>
      <div className="rec-dash-chart-container">
        <ChartComponent data={data} options={options} />
      </div>
    </div>
  );
}

function Dashboard() {
  const { gym } = useGymSettings();
  const todayPk = pkTodayKey();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(() => todayDashRange());
  const [applied, setApplied] = useState(() => todayDashRange());

  useEffect(() => {
    if (draft.startDate !== todayPk || draft.endDate !== todayPk) {
      setDraft(todayDashRange());
    }
    if (applied.startDate !== todayPk || applied.endDate !== todayPk) {
      setApplied(todayDashRange());
    }
  }, [todayPk, draft.startDate, draft.endDate, applied.startDate, applied.endDate]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setError('');
        setLoading(true);
        const data = await fetchDashboardOverview(applied);
        if (active) setOverview(data);
      } catch (err) {
        if (active) setError(err.message || 'Failed to load dashboard');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [applied]);

  const charts = useMemo(() => {
    if (!overview) return [];
    const { charts: c } = overview;
    const list = [];

    if (c.categorySales?.labels?.length) {
      list.push({
        title: 'Sales by Category',
        type: 'doughnut',
        data: buildPieDataset(c.categorySales.labels, c.categorySales.values),
        options: pieOptions,
      });
    }

    list.push({
      title: 'Daily Collection',
      type: 'bar',
      data: buildBarDataset('Daily Collection', c.dailyCollection.values, c.dailyCollection.labels, CHART_COLLECTION),
      options: collectionChartOptions,
    });

    list.push({
      title: 'Sales by Day',
      type: 'bar',
      data: buildBarDataset('Sales', c.salesByDay.values, c.salesByDay.labels, CHART_SUBSCRIPTIONS),
      options: collectionChartOptions,
    });

    if (c.membershipVsRenewal?.labels?.length) {
      list.push({
        title: 'New Membership vs Renewal',
        type: 'bar',
        data: buildCompareBar(
          c.membershipVsRenewal.labels,
          c.membershipVsRenewal.values,
          [CHART_SUBSCRIPTIONS, CHART_RENEWAL],
        ),
        options: collectionChartOptions,
      });
    }

    if (c.subscriptionTrend?.labels?.length) {
      list.push({
        title: 'New Memberships (count)',
        type: 'bar',
        data: buildBarDataset('New Memberships', c.subscriptionTrend.values, c.subscriptionTrend.labels, CHART_SUBSCRIPTIONS),
        options: chartOptions,
      });
    }

    if (c.registrationTrend?.labels?.length) {
      list.push({
        title: 'Member Registrations',
        type: 'line',
        data: buildLineDataset('New Registrations', c.registrationTrend.values, c.registrationTrend.labels, CHART_MEMBERS),
        options: chartOptions,
        full: true,
      });
    }

    return list;
  }, [overview]);

  const gridCharts = charts.filter((c) => !c.full);
  const fullCharts = charts.filter((c) => c.full);
  const dashboardStats = overview?.stats || [];
  const rangeActive = Boolean(applied.startDate || applied.endDate);
  const gymName = String(gym?.name || '').trim();
  const subtitle = gymName
    ? `${gymName} — members, memberships, sales, and revenue`
    : 'Overview of members, memberships, sales, and revenue';

  const handleSearch = (e) => {
    e.preventDefault();
    const today = todayDashRange();
    setDraft(today);
    setApplied(today);
  };

  const handleClear = () => {
    const today = todayDashRange();
    setDraft(today);
    setApplied(today);
  };

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={subtitle} variant="banner" />

      <form className="panel-dash-filters" onSubmit={handleSearch}>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="rec-dash-start">Start date</label>
          <input
            id="rec-dash-start"
            type="date"
            className="owner-form-input"
            value={todayPk}
            max={todayPk}
            readOnly
            disabled
            onChange={() => {}}
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="rec-dash-end">End date</label>
          <input
            id="rec-dash-end"
            type="date"
            className="owner-form-input"
            value={todayPk}
            max={todayPk}
            readOnly
            disabled
            onChange={() => {}}
          />
        </div>
        <div className="panel-dash-filter-actions">
          <button type="submit" className="owner-btn owner-btn-primary owner-btn-sm">Search</button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleClear}>Clear</button>
        </div>
      </form>

      {rangeActive && (
        <p className="owner-placeholder-text panel-dash-applied">
          Pakistan time · today · {todayPk}
        </p>
      )}

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading dashboard...</p>}

      <div className="rec-dash-stat-grid rec-panel-stat-grid rec-panel-stat-grid-4">
        {dashboardStats.map((stat) => {
          const Icon = statIcons[stat.key] || MdCheckCircle;
          return (
            <PanelStatCard
              key={stat.key}
              statKey={stat.key}
              label={stat.label}
              value={stat.value}
              change={stat.change}
              variant={stat.variant}
              icon={Icon}
            />
          );
        })}
      </div>

      <div className="owner-card owner-mb-28 rec-dash-charts-card rec-panel-card">
        <h2 className="owner-card-title rec-dash-charts-heading">Sales Analytics</h2>
        <div className="rec-dash-charts-body">
          <div className="rec-dash-charts-grid rec-dash-charts-grid-extended">
            {gridCharts.map((chart) => (
              <ChartBlock key={chart.title} {...chart} />
            ))}
          </div>
          {fullCharts.map((chart) => (
            <ChartBlock key={chart.title} {...chart} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
